import type {
  Account,
  BusinessType,
  FilePurpose,
  Moov,
  NewBankAccount,
} from '@moovio/moov-js';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  DEFAULT_ERROR_MESSAGE,
  getError,
  NOT_AVAILABLE_ERROR_MESSAGE,
} from '../superpay/MoovError';
import { logPaymentError } from '../superpay/PaymentLogger';
import {
  MoovInformation,
  MoovScope,
  NewCarrierBankAccount,
  NewCarrierMoovAccount,
  SearchRoutingNumberResponse,
} from './MoovDTO';
import {
  generateMoovTokenAndGetAccount,
  useSyncSuperPayOnboardingStep,
} from './SuperPayAPI';

async function getMoov(accessToken: string) {
  try {
    const { loadMoov } = await import('@moovio/moov-js');
    return await loadMoov(accessToken);
  } catch {
    throw new Error(NOT_AVAILABLE_ERROR_MESSAGE);
  }
}

async function actionWrapper<T>(
  scope: MoovScope,
  errorSource:
    | 'createCarrierAccount'
    | 'createCarrierBankAccount'
    | 'completeInstantBankAccountVerification'
    | 'uploadMicroDepositScreenshot'
    | 'bankNameByRoutingNumber'
    | 'accountLegalName',
  action: (moov: Moov, moovInformation: MoovInformation) => Promise<T>,
) {
  let moovInformation: MoovInformation | null = null;

  try {
    moovInformation = await generateMoovTokenAndGetAccount(scope);
    const moov = await getMoov(moovInformation.access_token);

    if (!moov) {
      throw new Error(DEFAULT_ERROR_MESSAGE);
    }

    return await action(moov, moovInformation);
  } catch (error: unknown) {
    const preparedError = getError(error);

    logPaymentError(preparedError as Error, `MoovAPI.${errorSource}`, {
      moovAccountId: moovInformation?.moov_account_id,
    });

    return Promise.reject(preparedError);
  }
}

export function useCreateCarrierMoovAccount() {
  const { mutateAsync: syncSuperPayOnboardingStep } =
    useSyncSuperPayOnboardingStep();

  return useMutation({
    mutationFn: (newAccountData: NewCarrierMoovAccount) => {
      const newAccount: Partial<Account> = {
        accountType: 'business',
        profile: {
          business: {
            businessType: newAccountData.businessType as BusinessType,
            description: newAccountData.description,
            legalBusinessName: newAccountData.legalBusinessName,
            taxID: { ein: { number: newAccountData.einNumber } },
          },
        },
        metadata: { guid: newAccountData.carrier_guid, type: 'carrier' },
        foreignID: newAccountData.carrier_guid,
      };

      const scope = 'account';

      return actionWrapper(scope, 'createCarrierAccount', async (moov) => {
        const account = await moov.accounts.create(newAccount);

        return syncSuperPayOnboardingStep({
          scope,
          moov_bank_account_id: null,
          moov_account_id: account.accountID,
        }).finally(() => account);
      });
    },
  });
}

export function useCreateBankAccount() {
  const { mutateAsync: syncSuperPayOnboardingStep } =
    useSyncSuperPayOnboardingStep();

  return useMutation({
    mutationFn: (newBankAccountData: NewCarrierBankAccount) => {
      const newBankAccount: NewBankAccount = {
        routingNumber: newBankAccountData.routingNumber,
        accountNumber: newBankAccountData.accountNumber,
        holderName: newBankAccountData.holderName,
        holderType: 'business',
        bankAccountType: 'checking',
      };

      const scope = 'bank_account';

      return actionWrapper(
        scope,
        'createCarrierBankAccount',
        async (moov, moovInformation) => {
          const bankAccount = await moov.accounts.bankAccounts.link({
            accountID: moovInformation.moov_account_id || '',
            bankAccount: newBankAccount,
          });

          return syncSuperPayOnboardingStep({
            scope,
            moov_bank_account_id: bankAccount.bankAccountID,
            moov_account_id: moovInformation.moov_account_id,
          }).finally(() => bankAccount);
        },
      );
    },
  });
}

export function useCompleteInstantBankAccountVerification() {
  const { mutateAsync: syncSuperPayOnboardingStep } =
    useSyncSuperPayOnboardingStep();

  return useMutation({
    mutationFn: (code: string) => {
      const scope = 'micro_deposit';

      return actionWrapper(
        scope,
        'completeInstantBankAccountVerification',
        async (moov, moovInformation) => {
          const result = await moov.accounts.bankAccounts.completeVerification({
            accountID: moovInformation.moov_account_id || '',
            bankAccountID: moovInformation.moov_bank_account_id || '',
            code,
          });

          return syncSuperPayOnboardingStep({
            scope,
            moov_bank_account_id: moovInformation.moov_bank_account_id,
            moov_account_id: moovInformation.moov_account_id,
          }).finally(() => result);
        },
      );
    },
  });
}

export function useUploadMicroDepositScreenshot() {
  const { mutateAsync: syncSuperPayOnboardingStep } =
    useSyncSuperPayOnboardingStep();

  return useMutation({
    mutationFn: (file: File) => {
      const scope = 'file';

      return actionWrapper(
        scope,
        'uploadMicroDepositScreenshot',
        async (moov, moovInformation) => {
          const uploadedFile = await moov.accounts.files.upload({
            accountID: moovInformation.moov_account_id || '',
            file,
            filePurpose: 'individual_verification' as FilePurpose,
          });

          return syncSuperPayOnboardingStep({
            scope,
            moov_bank_account_id: moovInformation.moov_bank_account_id,
            moov_account_id: moovInformation.moov_account_id,
          }).finally(() => uploadedFile);
        },
      );
    },
  });
}

export function useBankNameByRoutingNumber() {
  return useMutation({
    mutationFn: (routingNumber: string) => {
      return actionWrapper('account', 'bankNameByRoutingNumber', (moov) => {
        return moov.institutions
          .lookupByRoutingNumber(routingNumber)
          .then((response: SearchRoutingNumberResponse) => {
            return response.result?.[0]?.customerName;
          });
      });
    },
  });
}

export function useMoovAvailable() {
  const [isAvailable, setIsAvailable] = useState(true);

  const setAvailable = async () => {
    try {
      const moovInformation = await generateMoovTokenAndGetAccount('account');
      await getMoov(moovInformation.access_token);
      setIsAvailable(true);
    } catch {
      setIsAvailable(false);
    }
  };

  useEffect(() => {
    void setAvailable();
  }, []);

  return isAvailable;
}
